import {
  FileUploadedStatus,
  Formik,
  FormikInsurance,
  typedMemo,
} from "src/types";
import { useEntryGroupStep2 } from "./entry-group-step-2.hook";
import { Button, InputUploadFile, Modal } from "src/ui/widgets";
import { Trans } from "react-i18next";
import { DownloadIconUnderscoreItas, RightArrowIcon } from "src/assets";
import { AnyObjectSchema } from "yup";
import { Insured } from "../../manage-data-normal/data-entry-step-2/components/insured/insured";
import { GroupList } from "./components/group-list/group-list";
import { GroupErrorBanner } from "./components/error-banner/group-error-banner";

export const EntryGroupStep2 = typedMemo(
  ({
    insuranceFormik,
    validationSchema,
  }: {
    insuranceFormik: Formik<FormikInsurance>;
    validationSchema: AnyObjectSchema;
  }) => {
    const {
      t,
      fileGroup,
      groupInfo,
      isModalVisible,
      modalFormik,
      isGroupErrorBanner,
      isInsuredDuplicatedError,
      onShowModalEditPerson,
      hideModal,
      onRemoveGroupInfoFile,
      setFileGroup,
      onDownloadFile,
      onIdentificationFieldsChanges,
    } = useEntryGroupStep2({ insuranceFormik, validationSchema });

    const { handleSubmit } = modalFormik;

    const { fileName, filesStatus } = groupInfo;

    const Title = (
      <div className="col-12 col-md-6 p-0 mb-3 my-4 font-16-bold text-uppercase">
        {t("entry_group_step_2.title")}
      </div>
    );

    const Upload = (
      <>
        <div className="col-12 p-0 mb-2 mb-md-1">
          <div className="font-16-light">
            {t("entry_group_step_2.upload_file_title_p1")}
          </div>
          <div className="font-16-light">
            {t("entry_group_step_2.upload_file_title_p2")}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-center  mt-4 mb-md-4">
          <div className="col-12">
            <InputUploadFile
              selectedFile={fileGroup}
              onFileSelected={
                setFileGroup as React.Dispatch<React.SetStateAction<File>>
              }
              onRemove={onRemoveGroupInfoFile}
              selectedFileName={fileName}
              fileStatus={filesStatus}
              showAlert={true}
            />
          </div>
        </div>
      </>
    );

    const GroupInfoTitle = (
      <div className="col-12 p-0 mb-2 mb-4 mt-5 font-18-light">
        {t("entry_group_step_2.group_info_title")}
      </div>
    );

    const GroupInfoBody = (
      <div className="d-flex flex-md-row flex-column gap-5 mb-5">
        <div className="group-info-container">
          <div className="group-square">1</div>
          <div className="group-info-title">
            {t("entry_group_step_2.group_info_element.title_1")}
          </div>
          <div className="font-16-light">
            {t("entry_group_step_2.group_info_element.body_1")}
          </div>
          <Button
            classes="group-info-button"
            classesText="text-reset"
            iconLeft={DownloadIconUnderscoreItas}
            classesIconLeft="filter-f5bb78"
            text={t("entry_group_step_2.group_info_element.button_label_1")}
            onPress={onDownloadFile}
          />
        </div>

        <div className="group-info-container">
          <div className="group-square">2</div>
          <div className="group-info-title">
            {t("entry_group_step_2.group_info_element.title_2")}
          </div>
          <div className="font-16-light">
            {t("entry_group_step_2.group_info_element.body_2")}
          </div>
        </div>

        <div className="group-info-container">
          <div className="group-square">3</div>
          <div className="group-info-title">
            {t("entry_group_step_2.group_info_element.title_3")}
          </div>
          <div className="font-16-light">
            {t("entry_group_step_2.group_info_element.body_3")}
            <Trans
              i18nKey="entry_group_step_2.group_info_element.list"
              components={{ ul: <ul className="font-16-light" />, li: <li /> }}
            />
          </div>
        </div>
      </div>
    );

    const GroupFileInfo = (
      <>
        {GroupInfoTitle}
        {GroupInfoBody}
      </>
    );

    const ModalEditPerson = (
      <Modal
        visible={isModalVisible}
        onClose={hideModal}
        classes="group-modify-modal-container"
      >
        <div className="font-18-semibold col-sm-12 col-md-6 p-0 px-md-3 pl-md-3 pr-md-3">
          {t("entry_group_step_2.modal_edit_title")}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mt-3 mb-4 border-top" />
          <Insured
            formik={modalFormik}
            validationSchema={validationSchema}
            base={""}
            insuredValue={modalFormik.values}
            insuredTouched={modalFormik.touched}
            insuredError={modalFormik.errors}
            onIdentificationFieldsChanges={onIdentificationFieldsChanges}
          />
          <div className="mt-4 mb-4 border-top" />
          <div
            className={`col-sm-12 d-flex align-items-center ${
              isInsuredDuplicatedError
                ? "justify-content-between"
                : "justify-content-end"
            }`}
          >
            {isInsuredDuplicatedError && <div className="c-error font-medium-s">{isInsuredDuplicatedError}</div>}
            <Button
              text={t("commons.confirm")}
              flavor="accent"
              type="submit"
              iconSize={20}
              iconRight={RightArrowIcon}
              classes=" pt-2 pb-2 d-flex align-items-center primary"
              classesText="font-15-semibold text-uppercase"
              classesIconRight="ms-2"
              disabled={!!isInsuredDuplicatedError}
            />
          </div>
        </form>
      </Modal>
    );

    return (
      <div>
        {Title}
        {Upload}
        {groupInfo.filesStatus && (
          <>
            {isGroupErrorBanner ? (
              <GroupErrorBanner fileStatus={groupInfo.filesStatus} />
            ) : (
              <GroupList
                groupInfo={groupInfo}
                onModifyElement={onShowModalEditPerson}
              />
            )}
          </>
        )}
        {ModalEditPerson}
        {filesStatus !== FileUploadedStatus.OK && GroupFileInfo}
      </div>
    );
  }
);
